import { Injectable } from '@angular/core';
import { BackendService } from '@tg4/http-infrastructure/dist/src';
import { Observable } from 'rxjs';
import { GoalTypeEnum } from '../shared/enums/GoalTypeEnum';
import { GoalFeedback } from './_interfaces/goal-feedback.interface';
import { Goal } from './_interfaces/goal.interface';
import { TwoPlusTwoCoaching } from './_interfaces/two-plus-two-coaching.interface';
import { ProcessTypeEnum } from '../shared/interfaces/process.interface';

@Injectable({ providedIn: 'root' })
export class GoalsService {

    constructor(private _httpService: BackendService) {
    }

    public getUserGoals(cycle: number, goalType: GoalTypeEnum = GoalTypeEnum.Input): Observable<any> {
        return this._httpService.get('getUserGoals', [], [
            { 'name': 'cycle', 'value': cycle as any },
            { 'name': 'goalType', 'value': goalType as any }
        ]);
    }

    public getUserGoalsForInput(cycleId: number): Observable<any> {
        return this._httpService.get('getUserGoalsForInput', [], [
            { 'name': 'cycleId', 'value': cycleId as any }
        ]);
    }

    public getGoalById(goalId: number): Observable<any> {
        return this._httpService.get('getGoalById', [], [
            { 'name': 'goalId', 'value': goalId as any }
        ]);
    }

    public getConsentProcessGoals(processId: number): Observable<any> {
        return this._httpService.get('getConsentProcessGoals', [], [
            { 'name': 'processId', 'value': processId as any }
        ]);
    }

    public addNewGoal(newGoal: Goal): Observable<any> {
        return this._httpService.post('addNewGoal', newGoal);
    }

    public addConsentGoal(newGoal: Goal): Observable<any> {
        return this._httpService.post('addConsentGoal', newGoal);
    }

    public updateGoal(goal: Goal): Observable<any> {
        return this._httpService.put('updateGoal', goal);
    }

    public deleteGoal(goalId: number, fromTwoPlusTwo: boolean = false): Observable<any> {
        return this._httpService.delete('deleteGoal', [
            { 'name': 'id', 'value': goalId as any },
            { 'name': 'fromTwoPlusTwo', 'value': fromTwoPlusTwo as any }
        ]);
    }

    public submitGoals(goalsIds: Array<number>, cycleParticipantId: number): Observable<any> {
        return this._httpService.post('submitGoals', {
            'goalIds': goalsIds,
            'cycleParticipantId': cycleParticipantId
        });
    }

    public getSubordinatesForValidation(cycleId: number): Observable<any> {
        return this._httpService.get('getSubordinates', [], [
            { 'name': 'cycleId', 'value': cycleId as any }
        ]);
    }

    public getConsentAttributes(cycleId: number): Observable<any> {
        return this._httpService.get('getConsentAttributes', [], [
            { 'name': 'cycleId', 'value': cycleId as any }
        ]);
    }

    public getSubordinateGoals(cycleId: number, userId: string): Observable<any> {
        return this._httpService.get('getSubordinateGoals', [], [
            { 'name': 'cycleId', 'value': cycleId as any },
            { 'name': 'userId', 'value': userId }
        ]);
    }

    public setGoalFeedback(participantId: string, feedbacks: Array<GoalFeedback>): Observable<any> {
        return this._httpService.post('setGoalFeedback', {
            'cycleParticipantId': participantId,
            'feedbacks': feedbacks
        });
    }

    public getUserGoalsForReview(cycleId: number, employeeId: string = null): Observable<any> {
        return this._httpService.get('getUserGoalsForReview', [], [
            { 'name': 'cycleId', 'value': cycleId as any },
            { 'name': 'userId', 'value': employeeId as any }
        ]);
    }

    public updateGoalsForReview(goal: Goal): Observable<any> {
        return this._httpService.put('updateGoalsForReview', goal);
    }

    public manageCoachingRecord(coaching: TwoPlusTwoCoaching): Observable<any> {
        return this._httpService.post('manageCoachingRecord', coaching);
    }

    public submitGoalsReview(goalsIds: Array<number>, coaching: TwoPlusTwoCoaching, cycleId: number, istwoPlusTwoCoachingIntegration: boolean): Observable<any> {
        return this._httpService.post('submitGoalsReview', {
            'goalIds': goalsIds,
            'twoPlusTwoCoaching': coaching,
            "cycleId": cycleId,
            'istwoPlusTwoCoachingIntegration': istwoPlusTwoCoachingIntegration
        });
    }

    public getSubordinatesForReview(cycleId: number): Observable<any> {
        return this._httpService.get('getSubordinatesForReview', [], [
            { 'name': 'cycleId', 'value': cycleId as any }
        ]);
    }

    public saveGoalsReviewValidation(
            participantId: string, coaching: TwoPlusTwoCoaching, feedbacks: Array<GoalFeedback>, istwoPlusTwoCoachingIntegration: boolean, allRequiredAnswered: boolean
    ): Observable<any> {
        return this._httpService.post('saveGoalsReviewValidation', {
            'cycleParticipantId': participantId,
            'twoPlusTwoCoachingValidation': coaching,
            'goalsFeedback': feedbacks,
            'isTwoPlusTwoCoachingIntegration': istwoPlusTwoCoachingIntegration,
            'allRequiredAnswered': allRequiredAnswered
        });
    }

    public reopenGoalsReviewValidationForUser(cycleId: number, userId: string): Observable<any> {
        return this._httpService.post('reopenGoalsReviewForUser', {
            'cycleId': cycleId,
            'userId': userId
        });
    }

    public reopenGoalsValidationForUser(cycleId: number, userId: string): Observable<any> {
        return this._httpService.post('reopenGoalInputValidationForUser', {
            'cycleId': cycleId,
            'userId': userId
        });
    }
}
